/**
 * KAN-776: чистые функции массового импорта архивов продавца (партия).
 * Без React и I/O: multipart для plan/ и import-batch/, отбор файлов на
 * клиенте, подсчёт строк партии, тексты статусов и ошибок.
 * KAN-777: дубли (isDuplicate, countDuplicates, describeDuplicates) и отбор
 * файлов на старт (importableFiles).
 * Одиночный импорт и общие поля мастера: lib/telegramConnect.
 */
import {
  appendImportCommonFields,
  appendLabel,
  describeApiError,
  describeInspectDuplicate,
  validateArchiveZip,
} from '@/lib/telegramConnect';
import type {
  BatchImportItem,
  BatchImportStatus,
  BatchItemStatus,
  BatchPlanItem,
  BatchPlanResult,
  BatchProxyWarning,
} from '@/types/telegramAccount';

/** Опрос хода партии (GET import-batch/{id}/), мс. */
export const BATCH_POLL_MS = 3000;

/** Лимит сервера: архивов в одной партии. */
export const MAX_BATCH_FILES = 30;
/** Лимит nginx на запрос партии (210 МБ): суммарно архивов до 200 МБ. */
export const MAX_BATCH_TOTAL_BYTES = 200 * 1024 * 1024;

export const TOO_MANY_FILES_TEXT = `В партии до ${MAX_BATCH_FILES} архивов, выберите меньше файлов`;
export const TOO_BIG_BATCH_TEXT = 'Суммарный размер архивов до 200 МБ, выберите меньше файлов';

/**
 * Коды ошибок импорта (api/services/telegram_account_connect.py, ConnectError;
 * коды партии из telegram_account_batch_import.py) на русском. Сервер обычно
 * присылает и текст (error_text), карта на случай, если текста нет.
 */
export const IMPORT_ERROR_LABELS: Record<string, string> = {
  bad_archive: 'Архив не разобран: нет папки tdata и файлов .session и .json',
  bad_tdata: 'Папка tdata повреждена или не читается',
  bad_session: 'Файл сессии повреждён или не читается',
  bad_seller_json: 'JSON продавца некорректен',
  no_app_keys: 'В архиве нет api_id и api_hash приложения',
  no_phone: 'Номер телефона не найден',
  no_proxy: 'Нет рабочего прокси для страны номера',
  duplicate: 'Номер уже подключён в кабинете',
  duplicate_account: 'Номер уже подключён в кабинете',
  duplicate_in_batch: 'Этот аккаунт уже есть в партии',
  decommissioned: 'Номер есть в кабинете как списанный',
  file_too_large: 'Архив больше допустимого размера',
  too_many_files: `Слишком много архивов: в партии до ${MAX_BATCH_FILES}`,
  gateway_unavailable: 'Держатель сессий недоступен',
  gateway_config: 'Держатель сессий не настроен',
  bad_gateway_reply: 'Держатель сессий ответил неожиданно',
  proxy_unreachable: 'Прокси недоступен',
  fresh_session: 'Сессия слишком свежая, вход отложен',
  refused: 'Telegram отклонил сессию',
  refused_fresh: 'Telegram отклонил свежую сессию',
  account_dead: 'Сессия отозвана Telegram',
  label_prefix_unknown: 'Префикс метки не найден',
  seller_unknown: 'Продавец не найден',
  operator_unknown: 'Кампания не найдена',
  operator_required: 'Выберите кампанию',
  batch_unknown: 'Партия не найдена или устарела',
  not_started: 'Не запускался: партия остановлена',
  internal: 'Внутренняя ошибка сервера',
};

export const BATCH_ITEM_STATUS_LABELS: Record<BatchItemStatus, string> = {
  queued: 'в очереди',
  running: 'подключается',
  imported: 'подключён',
  skipped: 'пропущен',
  failed: 'ошибка',
  not_started: 'не запускался',
};

// ------------------------------------------------------------- файлы

export interface RejectedArchive {
  file: File;
  problem: string;
}

/** Zip-файлы в партию, остальные с причиной отказа (та же проверка, что у одного архива). */
export function partitionArchiveFiles(files: File[]): {
  accepted: File[];
  rejected: RejectedArchive[];
} {
  const accepted: File[] = [];
  const rejected: RejectedArchive[] = [];
  for (const file of files) {
    const problem = validateArchiveZip(file);
    if (problem) rejected.push({ file, problem });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

/**
 * Лимиты партии до запроса plan/: число архивов и суммарный размер
 * (принятых файлов). Текст ошибки или null; при ошибке запрос не отправляется.
 */
export function validateBatchFiles(files: Array<{ size: number }>): string | null {
  if (files.length > MAX_BATCH_FILES) return TOO_MANY_FILES_TEXT;
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_BATCH_TOTAL_BYTES) return TOO_BIG_BATCH_TEXT;
  return null;
}

// ------------------------------------------------------- общие поля

/** Общие поля партии (BatchCommonFields): префикс метки на всю партию и флаги. */
export interface BatchCommonValues {
  /** Код префикса; пусто - сервер подбирает по стране номера. */
  labelPrefix: string;
  enabled: boolean;
  outreach: boolean;
}

/** Как у одиночного импорта (KAN-722): включён, первым не пишет. */
export const DEFAULT_BATCH_COMMON: BatchCommonValues = {
  labelPrefix: '',
  enabled: true,
  outreach: false,
};

// --------------------------------------------------------- тела запросов

/** multipart для POST import-batch/plan/: поле archives повторяется на каждый файл. */
export function buildBatchPlanFormData(files: File[], operatorId?: string): FormData {
  const fd = new FormData();
  for (const file of files) fd.append('archives', file);
  if (operatorId) fd.append('operator_id', operatorId);
  return fd;
}

export interface BatchImportFields {
  archives: File[];
  /** Продавец обязателен: партия без него не стартует. */
  sellerId: string;
  operatorId?: string;
  enabled: boolean;
  outreach: boolean;
  skipSpamCheck: boolean;
  had2fa: boolean;
  /** Стоимость покупки одного аккаунта, ₽, текстом поля. */
  purchaseCost?: string;
  /** Код префикса метки на всю партию; пусто - сервер подбирает по стране. */
  labelPrefix?: string;
}

/** multipart для POST import-batch/: архивы, общие поля импорта и префикс метки на партию. */
export function buildBatchImportFormData(f: BatchImportFields): FormData {
  const fd = new FormData();
  for (const file of f.archives) fd.append('archives', file);
  appendLabel(fd, '', f.labelPrefix);
  appendImportCommonFields(fd, f);
  return fd;
}

// --------------------------------------------------------------- план

/**
 * Почему строка плана не войдёт в партию (столбец "Причина"): null - войдёт.
 * Ошибка разбора (error_code) здесь не описывается: в том же столбце её
 * показывает describeBatchItemError. KAN-777: дубли с конкретикой (метка в
 * кабинете или первый файл партии с тем же номером или содержимым).
 */
export function describePlanSkip(item: BatchPlanItem): string | null {
  if (item.error_code) return null;
  const duplicate = describeInspectDuplicate(item.duplicate);
  if (duplicate && item.duplicate) {
    const label = item.duplicate.label;
    return item.duplicate.decommissioned
      ? `дубль: списан в кабинете как ${label}`
      : `дубль: уже в кабинете как ${label}`;
  }
  if (item.duplicate_in_batch) {
    return item.duplicate_of
      ? `дубль: повтор файла ${item.duplicate_of}`
      : 'дубль: повтор номера в партии';
  }
  if (!item.phone) return 'нет номера';
  if (item.proxy_reason === 'no_proxy' || item.proxy_id === null) return 'нет прокси';
  return null;
}

/** KAN-777: строка плана - дубль (номер уже в кабинете или повтор внутри партии). */
export function isDuplicate(
  item: Pick<BatchPlanItem, 'duplicate' | 'duplicate_in_batch'>
): boolean {
  return Boolean(item.duplicate) || Boolean(item.duplicate_in_batch);
}

export interface DuplicateCounts {
  total: number;
  /** Повторы внутри партии (номер или содержимое архива). */
  inBatch: number;
  /** Номер уже в кабинете: подключён или списан. */
  connected: number;
}

/**
 * KAN-777: счётчики дублей плана. Берутся из ответа сервера
 * (duplicates_in_batch, duplicates_connected), если он их прислал; старый ответ
 * без них считается по строкам: строка и в кабинете, и в партии - один
 * кабинетный дубль (как в describePlanSkip).
 */
export function countDuplicates(
  items: BatchPlanItem[],
  plan?: Pick<BatchPlanResult, 'duplicates_in_batch' | 'duplicates_connected'> | null
): DuplicateCounts {
  let inBatch: number;
  let connected: number;
  if (
    typeof plan?.duplicates_in_batch === 'number' &&
    typeof plan?.duplicates_connected === 'number'
  ) {
    inBatch = plan.duplicates_in_batch;
    connected = plan.duplicates_connected;
  } else {
    connected = items.filter((i) => i.duplicate).length;
    inBatch = items.filter((i) => !i.duplicate && i.duplicate_in_batch).length;
  }
  return { total: inBatch + connected, inBatch, connected };
}

/** "Дубли: 3 (в партии: 1, уже в кабинете: 2). В партию не войдут" или null, если дублей нет. */
export function describeDuplicates(counts: DuplicateCounts): string | null {
  if (counts.total <= 0) return null;
  return `Дубли: ${counts.total} (в партии: ${counts.inBatch}, уже в кабинете: ${counts.connected}). В партию не войдут`;
}

/** Код предупреждения строки и прокси: страна прокси в справочнике не распознана. */
export const UNKNOWN_COUNTRY = 'unknown_country';
export const PROXY_UNKNOWN_COUNTRY_MARK = 'страна не определена';

/** У строки плана прокси без распознанной страны (назначен как "другая страна"). */
export function hasUnknownCountryProxy(item: Pick<BatchPlanItem, 'warnings'>): boolean {
  return (item.warnings ?? []).includes(UNKNOWN_COUNTRY);
}

/**
 * Предупреждение под сводкой плана: "Прокси без страны: A, B. Проверьте
 * страну в справочнике прокси" или null, если таких прокси нет.
 */
export function describeProxyWarnings(
  warnings: BatchProxyWarning[] | null | undefined
): string | null {
  const labels = (warnings ?? [])
    .filter((w) => w.code === UNKNOWN_COUNTRY)
    .map((w) => w.proxy_label || `#${w.proxy_id}`);
  if (!labels.length) return null;
  return `Прокси без страны: ${labels.join(', ')}. Проверьте страну в справочнике прокси`;
}

/** Строка плана войдёт в партию: архив разобран, номер уникален и есть прокси. */
export function isImportable(item: BatchPlanItem): boolean {
  return !item.error_code && describePlanSkip(item) === null;
}

export function countImportable(items: BatchPlanItem[]): number {
  return items.filter(isImportable).length;
}

/**
 * KAN-777: файлы на старт партии: только строки плана, которые войдут
 * (isImportable). index строки = позиция файла в accepted (порядок тот же,
 * что уходил в plan/). Строка с индексом вне accepted отбрасывается.
 */
export function importableFiles(accepted: File[], items: BatchPlanItem[]): File[] {
  return items
    .filter(isImportable)
    .map((item) => accepted[item.index])
    .filter((file): file is File => file !== undefined);
}

/**
 * Текст ошибки строки: текст сервера, иначе код по карте, иначе сам код.
 * KAN-777: для duplicate_in_batch без текста сервера дописывается первый файл.
 */
export function describeBatchItemError(item: {
  error_code?: string | null;
  error_text?: string | null;
  duplicate_of?: string | null;
}): string | null {
  if (item.error_text?.trim()) return item.error_text.trim();
  if (!item.error_code) return null;
  const label = IMPORT_ERROR_LABELS[item.error_code] ?? item.error_code;
  if (item.error_code === 'duplicate_in_batch' && item.duplicate_of) {
    return `${label}: повтор файла ${item.duplicate_of}`;
  }
  return label;
}

// ------------------------------------------------------------- прогресс

/** Партию ещё опрашивать: она в очереди или идёт. */
export function isBatchActive(status: BatchImportStatus['status'] | undefined): boolean {
  return status === 'queued' || status === 'running';
}

/** Текст ошибки GET import-batch/{id}/: 403 - нет доступа к оператору партии. */
export function describeBatchStatusError(err: unknown): string {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === 403) return 'Нет доступа к этой партии';
  return describeApiError(err, 'Не удалось получить ход партии');
}

/** "Партия остановлена: держатель сессий недоступен" или null. */
export function describeStoppedReason(code: string | null | undefined): string | null {
  if (!code) return null;
  return `Партия остановлена: ${IMPORT_ERROR_LABELS[code] ?? code}`;
}

export interface BatchProgress {
  done: number;
  total: number;
  percent: number;
  imported: number;
  failed: number;
}

const FINISHED: ReadonlySet<BatchItemStatus> = new Set([
  'imported',
  'skipped',
  'failed',
  'not_started',
]);

export function batchProgress(items: BatchImportItem[]): BatchProgress {
  const total = items.length;
  const done = items.filter((i) => FINISHED.has(i.status)).length;
  const imported = items.filter((i) => i.status === 'imported').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  return {
    done,
    total,
    percent: total ? Math.round((done / total) * 100) : 0,
    imported,
    failed,
  };
}

/** 1 аккаунт, 2 аккаунта, 5 аккаунтов. */
export function pluralAccounts(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  let word = 'аккаунтов';
  if (abs < 11 || abs > 14) {
    if (last === 1) word = 'аккаунт';
    else if (last >= 2 && last <= 4) word = 'аккаунта';
  }
  return `${n} ${word}`;
}
